import { forwardRef } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  LogIn,
  LogOut,
  Settings as SettingsIcon,
  Shield,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { Avatar, getInitials } from "./Avatar";

/**
 * AuditLogItem — tenant audit log timeline element (Faza 8).
 *
 * Use-case:
 *   - Settings → Audit log sahifasi
 *   - Member activity history (drawer)
 *
 * Format: <timestamp> <actor> <action verb> <entity>
 *   Masalan: "12:34  Alibek  yangi hujjat yaratdi  knowledge.doc.42"
 *
 * Action turlari (icon + dot color):
 *   - create  — yashil (Plus)
 *   - update  — ko'k (Pencil)
 *   - delete  — qizil (Trash)
 *   - login   — violet (LogIn)
 *   - logout  — muted (LogOut)
 *   - config  — sky (SettingsIcon)
 *   - security— amber (Shield)
 *
 * Token'lar: --audit-log-* (tokens.css)
 */
export type AuditActionType =
  | "create"
  | "update"
  | "delete"
  | "login"
  | "logout"
  | "config"
  | "security"
  | "other";

const ACTION_META: Record<AuditActionType, { Icon: LucideIcon; dotVar: string }> = {
  create: { Icon: Plus, dotVar: "--audit-create-dot" },
  update: { Icon: Pencil, dotVar: "--audit-update-dot" },
  delete: { Icon: Trash2, dotVar: "--audit-delete-dot" },
  login: { Icon: LogIn, dotVar: "--audit-login-dot" },
  logout: { Icon: LogOut, dotVar: "--audit-log-dot" },
  config: { Icon: SettingsIcon, dotVar: "--audit-update-dot" },
  security: { Icon: Shield, dotVar: "--audit-delete-dot" },
  other: { Icon: SettingsIcon, dotVar: "--audit-log-dot" },
};

export interface AuditLogItemProps {
  /** ISO timestamp yoki formatted string */
  timestamp: string;
  /** Aktor ismi */
  actor: string;
  /** Aktor avatar URL */
  actorAvatarUrl?: string | null;
  /** Bajarilgan amal (verb): "yangi rol berdi", "o'chirdi" */
  action: string;
  /** Action turi (ikon va dot rang uchun) */
  actionType?: AuditActionType;
  /** Tegishli entity (highlighted): "user.42", "knowledge.doc.7" */
  entity?: string;
  /** Qo'shimcha izoh (ikkinchi qator) */
  details?: React.ReactNode;
  /** Mahalliylashtirgan vaqt sub-text (relative: "2 daqiqa avval") */
  relativeTime?: string;
  /** Variant: timeline (default — vertical rail+dot) | flat (oddiy row) */
  variant?: "timeline" | "flat";
  /** Oxirgi item — timeline rail'ni kesish uchun */
  isLast?: boolean;
  className?: string;
}

export const AuditLogItem = forwardRef<HTMLDivElement, AuditLogItemProps>(
  (
    {
      timestamp,
      actor,
      actorAvatarUrl,
      action,
      actionType = "other",
      entity,
      details,
      relativeTime,
      variant = "timeline",
      isLast = false,
      className,
    },
    ref,
  ) => {
    const meta = ACTION_META[actionType];

    if (variant === "flat") {
      return (
        <div
          ref={ref}
          className={cn(
            "flex items-start gap-3 px-3 py-2.5 border-b border-[var(--audit-log-divider)] hover:bg-[var(--audit-log-bg-hover)] transition-colors",
            className,
          )}
        >
          <Avatar size="sm">
            {actorAvatarUrl ? (
              <img src={actorAvatarUrl} alt={actor} className="h-full w-full object-cover" />
            ) : (
              <span className="flex h-full w-full items-center justify-center bg-[var(--accent-violet-soft)] text-[var(--accent-violet)] text-[10px] font-semibold">
                {getInitials(actor)}
              </span>
            )}
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-1.5 flex-wrap">
              <span className="text-sm font-medium text-[var(--audit-log-actor-fg)]">{actor}</span>
              <span className="text-sm text-[var(--audit-log-action-fg)]">{action}</span>
              {entity && (
                <span
                  className="inline-flex items-center rounded-md border px-1.5 py-0 text-[11px] font-mono"
                  style={{
                    background: "var(--audit-log-entity-bg)",
                    color: "var(--audit-log-entity-fg)",
                    borderColor: "var(--audit-log-entity-border)",
                  }}
                >
                  {entity}
                </span>
              )}
            </div>
            {details && (
              <div className="mt-0.5 text-xs text-[var(--text-secondary)] leading-snug">
                {details}
              </div>
            )}
          </div>
          <div className="shrink-0 text-right">
            <div className="text-[11px] tabular-nums text-[var(--audit-log-time-fg)]">
              {timestamp}
            </div>
            {relativeTime && (
              <div className="text-[10px] text-[var(--text-muted)]">{relativeTime}</div>
            )}
          </div>
        </div>
      );
    }

    // Timeline variant
    return (
      <div
        ref={ref}
        className={cn(
          "relative flex gap-3 pl-1 pb-4",
          isLast && "pb-0",
          className,
        )}
      >
        {/* Vertical rail */}
        {!isLast && (
          <span
            aria-hidden
            className="absolute left-[11px] top-6 bottom-0 w-px bg-[var(--audit-log-rail)]"
          />
        )}

        {/* Action icon dot */}
        <span
          aria-hidden
          className={cn(
            "relative z-10 mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border bg-[var(--bg-base)]",
            "border-[var(--border-subtle)]",
          )}
        >
          <span
            className="absolute inset-1 rounded-full opacity-15"
            style={{ backgroundColor: `var(${meta.dotVar})` }}
          />
          <meta.Icon
            className="relative h-3 w-3"
            style={{ color: `var(${meta.dotVar})` }}
            aria-hidden
          />
        </span>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-1.5 flex-wrap">
            <span className="text-sm font-medium text-[var(--audit-log-actor-fg)]">{actor}</span>
            <span className="text-sm text-[var(--audit-log-action-fg)]">{action}</span>
            {entity && (
              <span
                className="inline-flex items-center rounded-md border px-1.5 py-0 text-[11px] font-mono"
                style={{
                  background: "var(--audit-log-entity-bg)",
                  color: "var(--audit-log-entity-fg)",
                  borderColor: "var(--audit-log-entity-border)",
                }}
              >
                {entity}
              </span>
            )}
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-[11px] text-[var(--audit-log-time-fg)]">
            <span className="tabular-nums">{timestamp}</span>
            {relativeTime && (
              <>
                <span aria-hidden>·</span>
                <span>{relativeTime}</span>
              </>
            )}
          </div>
          {details && (
            <div className="mt-1 text-xs text-[var(--text-secondary)] leading-snug">
              {details}
            </div>
          )}
        </div>
      </div>
    );
  },
);
AuditLogItem.displayName = "AuditLogItem";
